"use client";

import { useEffect, useRef, useState } from "react";
import Script from "next/script";
// The Toaster mounts here rather than in the site layout so sonner only ships
// in the chunk of the one page that raises toasts — same call the old contact
// page made on main.
import { toast, Toaster } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ADS_FORM_SUBMIT_CONVERSION } from "@/lib/analytics/load-tags";
import { useRevealOnce } from "@/lib/hooks/use-reveal-once";
import { useTranslation } from "@/lib/hooks/use-translation";

/* The option lists submit their Thai labels as the stored value — exactly what
   the main-branch contact page sent — so leads from this section line up with
   the existing rows in the CMS contacts table. That is why these arrays stay
   here in Thai after the i18n pass: they are the *values*. The visible labels
   come from `home.contact.*` and are index-aligned with them, so an English
   visitor reads English and the CMS still receives the Thai key. */
const BUSINESS_TYPES = [
  "ร้านอาหาร / คาเฟ่",
  "โรงแรม / ที่พัก",
  "ร้านค้าปลีก",
  "บริการสุขภาพ / ความงาม",
  "อื่นๆ",
];

const PRODUCTS = ["ทั้งหมด", "Jelloducks POS", "Jelloducks CRM"];

const CHANNELS = [
  "Google",
  "Facebook / Instagram",
  "เพื่อนแนะนำ",
  "งาน Event / Expo",
  "อื่นๆ",
];

const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? "";

type GrecaptchaRenderParams = {
  sitekey: string;
  callback?: (token: string) => void;
  "expired-callback"?: () => void;
  "error-callback"?: () => void;
};

type Grecaptcha = {
  render: (
    container: HTMLElement | string,
    params: GrecaptchaRenderParams,
  ) => number;
  reset: (widgetId?: number) => void;
  getResponse: (widgetId?: number) => string;
};

declare global {
  interface Window {
    grecaptcha?: Grecaptcha;
    onRecaptchaLoad?: () => void;
  }
}

const EMPTY_FORM = {
  name: "",
  phone: "",
  email: "",
  businessType: "",
  product: "",
  channel: "",
  message: "",
};

/** Everything but the message. The selects' hidden native <select> doesn't
    reliably raise the browser's own "fill this in" bubble, so these are also
    checked in handleSubmit — and again by /api/contact. */
const REQUIRED_FIELDS = [
  "name",
  "phone",
  "email",
  "businessType",
  "product",
  "channel",
] as const;

/** Red asterisk after the label of a required field. Hidden from screen
    readers, which already announce the control's own `required` state. */
function RequiredMark() {
  return (
    <span aria-hidden className="text-[#E7000B]">
      *
    </span>
  );
}

/**
 * ContactCta — v2 section 9, the closing form. A single 880px frosted card on
 * a huge pill of blue wash (the comp's 2559x745 gradient ellipse, kept at its
 * drawn size and centered so wider screens just see more of its flanks).
 *
 * The form itself — checkbox reCAPTCHA rendered explicitly, POST to
 * /api/contact, conversion tags on success — is the main branch's contact page
 * logic carried over. The one departure is a single full-name field in place
 * of first + last; the API splits it back into the two stored columns, so the
 * CMS contacts table reads new rows the same as old ones.
 */
export default function ContactCta() {
  const { t } = useTranslation();
  const reveal = useRevealOnce<HTMLElement>(0.15);

  // Display labels for the three selects, index-aligned with the value arrays
  // at the top of this file. The value posted to /api/contact stays the Thai
  // string in every language; only what the reader sees changes.
  const optionLabels = (key: string) =>
    t(`home.contact.${key}`, { returnObjects: true }) as unknown as string[];
  const businessTypeLabels = optionLabels("businessTypes");
  const productLabels = optionLabels("products");
  const channelLabels = optionLabels("channels");
  const [form, setForm] = useState(EMPTY_FORM);
  /** False until the reader is close enough to the form to need the widget. */
  const [wantsRecaptcha, setWantsRecaptcha] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const recaptchaContainerRef = useRef<HTMLDivElement | null>(null);
  const recaptchaWidgetIdRef = useRef<number | null>(null);

  /**
   * Hold `api.js` back until the form is nearly on screen.
   *
   * This is the last section of a single-page site, so loading it
   * `afterInteractive` meant every visitor paid for reCAPTCHA — around half a
   * megabyte of Google JS, its own Roboto webfont, and a pile of third-party
   * cookies — during the initial load of a fold they may never scroll to.
   * Lighthouse counted it against main-thread work, font-display and
   * third-party cookies all at once.
   *
   * 600px of `rootMargin` arms it before the section arrives, whether the
   * reader scrolled down to it or a `#contact` CTA jumped them straight here,
   * so the widget still renders ahead of the first field — just not at the
   * expense of everyone who never reaches the form.
   */
  useEffect(() => {
    const el = reveal.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setWantsRecaptcha(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setWantsRecaptcha(true);
        io.disconnect();
      },
      { rootMargin: "600px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [reveal]);

  useEffect(() => {
    function renderWidget() {
      if (
        !window.grecaptcha ||
        !recaptchaContainerRef.current ||
        recaptchaWidgetIdRef.current !== null ||
        !RECAPTCHA_SITE_KEY
      ) {
        return;
      }
      recaptchaWidgetIdRef.current = window.grecaptcha.render(
        recaptchaContainerRef.current,
        {
          sitekey: RECAPTCHA_SITE_KEY,
          callback: (token: string) => setRecaptchaToken(token),
          "expired-callback": () => setRecaptchaToken(""),
          "error-callback": () => setRecaptchaToken(""),
        },
      );
    }

    window.onRecaptchaLoad = renderWidget;
    // The api.js script may already be live (bfcache restore, client-side
    // nav), in which case its onload callback has come and gone.
    if (window.grecaptcha?.render) renderWidget();
  }, []);

  function resetRecaptcha() {
    setRecaptchaToken("");
    if (window.grecaptcha && recaptchaWidgetIdRef.current !== null) {
      window.grecaptcha.reset(recaptchaWidgetIdRef.current);
    }
  }

  function handleChange(
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  function handleSelect(
    field: "businessType" | "product" | "channel",
    value: string,
  ) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    if (REQUIRED_FIELDS.some((field) => !form[field].trim())) {
      toast.error(t("home.contact.toast.required"));
      return;
    }

    if (!recaptchaToken) {
      toast.error(t("home.contact.toast.recaptcha"));
      return;
    }

    setIsSubmitting(true);

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, recaptchaToken }),
      });

      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as
          | { error?: string }
          | null;
        toast.error(
          data?.error === "recaptcha_failed"
            ? t("home.contact.toast.recaptchaFailed")
            : t("home.contact.toast.failed"),
        );
        resetRecaptcha();
        return;
      }

      toast.success(
        t("home.contact.toast.success"),
      );

      // Lead-conversion tracking — fired only on a successful submission (the
      // green success toast). Each tag is guarded so a blocked, consent-denied,
      // or not-yet-loaded script can't throw and break the form reset below.
      if (typeof window.gtag === "function") {
        window.gtag("event", "generate_lead", {
          event_category: "contact_form",
          event_label: "form_submit_success",
        });
        // Google Ads conversion — the "JelloDucks Form Submit" Event snippet.
        // Marketing's version wraps this in a `gtag_report_conversion()` that
        // returns false to hold a link/form navigation back until the hit is
        // sent; there is nothing to hold here (the submit is already a fetch
        // that resolved), so only the gtag call itself is carried over.
        window.gtag("event", "conversion", {
          send_to: ADS_FORM_SUBMIT_CONVERSION,
          value: 1.0,
          currency: "THB",
        });
      }
      if (typeof window.fbq === "function") {
        window.fbq("track", "Lead");
      }
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "form_submit_success" });

      setForm(EMPTY_FORM);
      resetRecaptcha();
    } catch {
      toast.error(t("home.contact.toast.failed"));
      resetRecaptcha();
    } finally {
      setIsSubmitting(false);
    }
  }

  // `scroll-mt-13` matches the fixed header's height: the two CTAs that link
  // here (navbar + hero) scroll #scroll-container, whose top edge sits *under*
  // the header, so without the margin the heading would land behind it.
  return (
    <section
      ref={reveal}
      id="contact"
      aria-label={t("home.contact.aria")}
      className="ct-reveal relative scroll-mt-13 overflow-hidden bg-[#FCFCFC]"
    >
      {wantsRecaptcha && (
        <Script
          src="https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit"
          strategy="afterInteractive"
          async
          defer
        />
      )}

      {/* The comp's wash: a 2559px-wide pill fading #FCFCFC → #E1F4FF, its top
          194px down so the card's upper half sits on plain ground. Centered
          rather than at the comp's absolute left so the symmetry holds at any
          viewport; the section crops the overhang.

          `bottom-0` rather than the comp's own 745px height, because the
          gradient has to *finish* at the section's bottom edge: the footer
          below opens on #E0F3FF, and the wash's last stop is what meets it.
          745px only lands there at the width the comp was drawn at. Stacked
          into one column the form is some 200px taller, so a fixed height ran
          the gradient out early and left a band of bare #FCFCFC between the
          wash and the footer — a visible seam across the join. Tied to the
          bottom instead, the two meet at every height. */}
      <div
        aria-hidden
        className="ct-wash pointer-events-none absolute bottom-0 left-1/2 top-[194px] w-[2559px] -translate-x-1/2 rounded-full bg-[linear-gradient(180deg,#FCFCFC_0%,#E1F4FF_100%)]"
      />

      <div className="relative mx-auto flex w-full max-w-[1440px] justify-center px-4 py-12 md:px-[80px] md:py-20">
        <div className="ct-card flex w-full max-w-[880px] flex-col items-center gap-6 rounded-[32px] bg-white/90 px-5 py-8 sm:rounded-[48px] sm:px-14 sm:py-12">
          <header
            style={{ "--ct-d": "0s" } as React.CSSProperties}
            className="ct-item flex flex-col items-center gap-2 text-center"
          >
            <h2 className="text-[26px]/[38px] font-semibold text-black [text-wrap:balance] sm:text-[32px]/[44px] lg:text-[40px]/[48px]">
              {t("home.contact.heading")}
            </h2>
            <p className="text-[17px]/[26px] text-black/60 [text-wrap:balance] sm:text-[20px]/[28px]">
              {t("home.contact.sub")}
            </p>
          </header>

          <form
            onSubmit={handleSubmit}
            className="flex w-full flex-col gap-3"
            aria-busy={isSubmitting}
          >
            {/* Row 1: full name, one field */}
            <div
              style={{ "--ct-d": "0.06s" } as React.CSSProperties}
              className="ct-item flex flex-col gap-1"
            >
              <Label htmlFor="ct-name" className="text-[#364153]">
                {t("home.contact.fields.name.label")}
                <RequiredMark />
              </Label>
              <Input
                id="ct-name"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder={t("home.contact.fields.name.placeholder")}
                autoComplete="name"
                required
                className="h-10 rounded-[8px] bg-white placeholder:text-black/40"
              />
            </div>

            {/* Row 2: phone + email */}
            <div
              style={{ "--ct-d": "0.12s" } as React.CSSProperties}
              className="ct-item flex flex-col gap-3 sm:flex-row sm:gap-6"
            >
              <div className="flex flex-1 flex-col gap-1">
                <Label htmlFor="ct-phone" className="text-[#364153]">
                  {t("home.contact.fields.phone.label")}
                  <RequiredMark />
                </Label>
                <Input
                  id="ct-phone"
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder={t("home.contact.fields.phone.placeholder")}
                  maxLength={10}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="tel"
                  required
                  className="h-10 rounded-[8px] bg-white placeholder:text-black/40"
                />
              </div>
              <div className="flex flex-1 flex-col gap-1">
                <Label htmlFor="ct-email" className="text-[#364153]">
                  {t("home.contact.fields.email.label")}
                  <RequiredMark />
                </Label>
                <Input
                  id="ct-email"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="example@email.com"
                  autoComplete="email"
                  required
                  className="h-10 rounded-[8px] bg-white placeholder:text-black/40"
                />
              </div>
            </div>

            {/* Row 3: business type + product */}
            <div
              style={{ "--ct-d": "0.18s" } as React.CSSProperties}
              className="ct-item flex flex-col gap-3 sm:flex-row sm:gap-6"
            >
              <div className="flex flex-1 flex-col gap-1">
                <Label htmlFor="ct-businessType" className="text-[#364153]">
                  {t("home.contact.fields.businessType.label")}
                  <RequiredMark />
                </Label>
                <Select
                  name="businessType"
                  required
                  value={form.businessType}
                  onValueChange={(v) => handleSelect("businessType", v)}
                >
                  <SelectTrigger
                    id="ct-businessType"
                    aria-required
                    className="w-full rounded-[8px] bg-white text-black data-[size=default]:h-10 data-[placeholder]:text-black/40"
                  >
                    <SelectValue
                      placeholder={t("home.contact.fields.businessType.placeholder")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {BUSINESS_TYPES.map((value, i) => (
                      <SelectItem key={value} value={value}>
                        {businessTypeLabels[i] ?? value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-1 flex-col gap-1">
                <Label htmlFor="ct-product" className="text-[#364153]">
                  {t("home.contact.fields.product.label")}
                  <RequiredMark />
                </Label>
                <Select
                  name="product"
                  required
                  value={form.product}
                  onValueChange={(v) => handleSelect("product", v)}
                >
                  <SelectTrigger
                    id="ct-product"
                    aria-required
                    className="w-full rounded-[8px] bg-white text-black data-[size=default]:h-10 data-[placeholder]:text-black/40"
                  >
                    <SelectValue
                      placeholder={t("home.contact.fields.product.placeholder")}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {PRODUCTS.map((value, i) => (
                      <SelectItem key={value} value={value}>
                        {productLabels[i] ?? value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 4: how they found us */}
            <div
              style={{ "--ct-d": "0.24s" } as React.CSSProperties}
              className="ct-item flex flex-col gap-1"
            >
              <Label htmlFor="ct-channel" className="text-[#364153]">
                {t("home.contact.fields.channel.label")}
                <RequiredMark />
              </Label>
              <Select
                name="channel"
                required
                value={form.channel}
                onValueChange={(v) => handleSelect("channel", v)}
              >
                <SelectTrigger
                  id="ct-channel"
                  aria-required
                  className="w-full rounded-[8px] bg-white text-black data-[size=default]:h-10 data-[placeholder]:text-black/40"
                >
                  <SelectValue
                    placeholder={t("home.contact.fields.channel.placeholder")}
                  />
                </SelectTrigger>
                <SelectContent>
                  {CHANNELS.map((value, i) => (
                    <SelectItem key={value} value={value}>
                      {channelLabels[i] ?? value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Row 5: message */}
            <div
              style={{ "--ct-d": "0.3s" } as React.CSSProperties}
              className="ct-item flex flex-col gap-1"
            >
              <Label htmlFor="ct-message" className="text-[#364153]">
                {t("home.contact.fields.message.label")}
              </Label>
              <Textarea
                id="ct-message"
                name="message"
                value={form.message}
                onChange={handleChange}
                placeholder={t("home.contact.fields.message.placeholder")}
                rows={2}
                className="min-h-[55px] resize-none rounded-[8px] bg-white placeholder:text-black/40"
              />
            </div>

            {/* Not in the comp, but the backend rejects a submission without
                its token, so the checkbox widget sits where main put it —
                between the fields and the button, kept to the left edge on the
                widths where the card is wider than the widget. */}
            <div
              style={{ "--ct-d": "0.36s" } as React.CSSProperties}
              className="ct-item flex justify-center sm:justify-start"
            >
              <div ref={recaptchaContainerRef} />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              style={{ "--ct-d": "0.42s" } as React.CSSProperties}
              className="ct-item ct-submit mt-3 w-full rounded-[14px] bg-primary-strong px-5 py-3 text-[18px]/[24px] font-medium text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting
                ? t("home.contact.submitting")
                : t("home.contact.submit")}
            </button>
          </form>
        </div>
      </div>

      <Toaster position="top-center" richColors closeButton />
    </section>
  );
}
